/**
 * AvisoCard.tsx: Card de aviso do painel comercial da tela de login.
 *
 * Específico do login (por isso vive aqui, não em components/ui): compõe
 * Card + Badge da biblioteca base com a editoria de avisos (categoria +
 * severidade). Dentro do escopo de tema do login, --color-accent é o ciano
 * da marca Órbita, então eyebrow, CTA e badge "info" herdam o ciano por
 * token, sem hex local. Sinais de severidade usam os tokens de status.
 */

import { Badge, Card } from "@/components/ui";
import type { BadgeTone } from "@/components/ui";
import type { Aviso, AvisoCategoria, AvisoSeveridade } from "./avisos";

// Sinal por severidade: tom semântico do Badge + rótulo executivo.
// info=acento (ciano no login) · sucesso=verde · atencao=âmbar · critico=vermelho.
const SEVERIDADE: Record<AvisoSeveridade, { tone: BadgeTone; rotulo: string }> = {
  critico: { tone: "red", rotulo: "Crítico" },
  atencao: { tone: "amber", rotulo: "Atenção" },
  sucesso: { tone: "green", rotulo: "Disponível" },
  info: { tone: "info", rotulo: "Informativo" },
};

// Rótulo curto por categoria: vira o eyebrow mono DENTRO de cada card, já que
// os slides são páginas de tamanho fixo. Cada card carrega a sua "trilha".
const CATEGORIA_ROTULO: Record<AvisoCategoria, string> = {
  plataforma: "Avisos da plataforma",
  novidade: "O que há de novo",
  comercial: "Disponível em breve",
};

// min-height comum a todos os cards: nenhum card estica diferente dos outros,
// independentemente do tamanho do resumo (padronização vertical dos slides).
const CARD_MIN_HEIGHT = "168px";

// Formata data ISO (YYYY-MM-DD) para exibição BR (DD/MM/AAAA).
function formatarData(iso: string): string {
  const [ano, mes, dia] = iso.split("-");
  if (!ano || !mes || !dia) return iso;
  return `${dia}/${mes}/${ano}`;
}

// ---------------------------------------------------------------------------
// AvisoCard: eyebrow da trilha + linha de sinal (badge por severidade) +
// título + resumo inteiro (sem corte) + rodapé (prazo + CTA). Borda-esquerda
// âmbar uniforme em todos os cards (o sinal vive no badge, não na borda).
// ---------------------------------------------------------------------------
export function AvisoCard({ aviso }: { aviso: Aviso }) {
  const sev = SEVERIDADE[aviso.severidade];
  const prazoFmt = aviso.prazo ? formatarData(aviso.prazo) : null;

  return (
    <Card
      style={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        // Padronização vertical: todo card tem a MESMA altura mínima; a altura
        // real cresce com o conteúdo (resumo inteiro, sem corte).
        minHeight: CARD_MIN_HEIGHT,
        boxSizing: "border-box",
        // Superfície translúcida sobre a galáxia: derivada do token --ink
        // (nenhum hex local). A borda padrão vem do Card (--color-bg-border).
        background: "color-mix(in srgb, var(--ink) 85%, transparent)",
        backdropFilter: "blur(6px)",
        WebkitBackdropFilter: "blur(6px)",
        borderLeft: "3px solid var(--color-status-amber)",
        padding: "1.125rem 1.25rem",
      }}
    >
      {/* Eyebrow da trilha (acento mono/uppercase) */}
      <span
        style={{
          fontFamily: '"IBM Plex Mono", monospace',
          fontSize: "0.625rem",
          fontWeight: 600,
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          color: "var(--color-accent)",
          margin: "0 0 0.625rem",
        }}
      >
        {CATEGORIA_ROTULO[aviso.categoria]}
      </span>

      {/* Linha de sinal: badge de severidade + tag opcional */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "0.5rem",
          marginBottom: "0.625rem",
        }}
      >
        <Badge tone={sev.tone} soft>
          {sev.rotulo}
        </Badge>
        {aviso.tag && (
          <span
            style={{
              fontFamily: '"IBM Plex Mono", monospace',
              fontSize: "0.625rem",
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              color: "var(--color-text-muted)",
            }}
          >
            {aviso.tag}
          </span>
        )}
      </div>

      {/* Título: âncora do card, peso 600 */}
      <h4
        style={{
          fontFamily: '"Space Grotesk", sans-serif',
          fontSize: "1.0625rem",
          fontWeight: 600,
          color: "var(--color-text-primary)",
          margin: "0 0 0.375rem",
        }}
      >
        {aviso.titulo}
      </h4>

      {/* Resumo: texto inteiro (sem clamp), o card cresce com o conteúdo */}
      <p
        style={{
          fontFamily: '"Inter", sans-serif',
          fontSize: "0.8125rem",
          color: "var(--color-text-secondary)",
          lineHeight: 1.5,
          margin: 0,
        }}
      >
        {aviso.resumo}
      </p>

      {/* Rodapé de valor/ação: prazo (meta) + CTA quando há ação.
          marginTop:auto ancora o rodapé na base do card (padronização). */}
      {(prazoFmt || aviso.acao) && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: "0.5rem",
            marginTop: "auto",
            paddingTop: "0.75rem",
          }}
        >
          {prazoFmt ? (
            <span
              style={{
                fontFamily: '"IBM Plex Mono", monospace',
                fontSize: "0.625rem",
                letterSpacing: "0.04em",
                color: "var(--color-text-muted)",
              }}
            >
              até {prazoFmt}
            </span>
          ) : (
            <span />
          )}
          {aviso.acao && (
            <span
              style={{
                fontFamily: '"Inter", sans-serif',
                fontSize: "0.75rem",
                fontWeight: 600,
                color: "var(--color-accent)",
              }}
            >
              {aviso.acao.rotulo} →
            </span>
          )}
        </div>
      )}
    </Card>
  );
}
